import { Box, Typography } from '@mui/material';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { Button, ModalContentContainer, TextField } from '@/components';
import { useForm } from '@/hooks';
import { EVENTS } from '@/services';
import { selectUserProfileResult, useUpdateUserProfileMutation } from '@/store';

export type EditProfileFormState = {
  firstName: string;
  lastName: string;
  jobTitle: string;
  companyName: string;
};

export const EditProfile = ({
  onSuccess,
  onCancel,
}: {
  onSuccess?: () => void;
  onCancel?: () => void;
}) => {
  const [updateProfile] = useUpdateUserProfileMutation();
  const { form, setForm, errors, setErrors, validateForm, onChange } =
    useForm<EditProfileFormState>({
      firstName: '',
      lastName: '',
      jobTitle: '',
      companyName: '',
    });
  const { data: profile } = useSelector(selectUserProfileResult);
  useEffect(() => {
    if (profile) {
      setForm({
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        jobTitle: profile.jobTitle || '',
        companyName: profile.companyName || '',
      });
    }
  }, [profile, setForm]);

  const onSubmit = async () => {
    const isValid = validateForm(['firstName', 'lastName']);

    if (!isValid) {
      return;
    }

    if (!profile?.email) {
      setErrors(errors => ({
        ...errors,
        companyName:
          'Something went wrong. Please try again or contact support.',
      }));
      return;
    }

    try {
      await updateProfile({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        jobTitle: form.jobTitle.trim(),
        companyName: form.companyName.trim(),
        email: profile?.email,
        event: EVENTS.PROFILE_EVENTS.UPDATE_DETAILS,
      }).unwrap();
      onSuccess?.();
    } catch {
      setErrors(errors => ({
        ...errors,
        companyName:
          'Something went wrong. Please try again or contact support.',
      }));
    }
  };

  return (
    <ModalContentContainer>
      <Typography variant="h1">Edit Profile</Typography>
      <TextField
        id="firstName"
        fullWidth
        label="First Name"
        placeholder="First Name"
        sx={{ mt: 6 }}
        value={form.firstName}
        error={!!errors.firstName}
        helperText={errors.firstName}
        onChange={onChange}
      />
      <TextField
        id="lastName"
        fullWidth
        label="Last Name"
        placeholder="Last Name"
        sx={{ mt: 2 }}
        value={form.lastName}
        error={!!errors.lastName}
        helperText={errors.lastName}
        onChange={onChange}
      />
      <TextField
        id="jobTitle"
        fullWidth
        label="Job Title"
        placeholder="Job Title"
        sx={{ mt: 2 }}
        value={form.jobTitle}
        error={!!errors.jobTitle}
        helperText={errors.jobTitle}
        onChange={onChange}
      />
      <TextField
        id="companyName"
        fullWidth
        label="Company Name"
        placeholder="Company Name"
        sx={{ mt: 2 }}
        value={form.companyName}
        error={!!errors.companyName}
        helperText={errors.companyName}
        onChange={onChange}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 2,
          mt: 4,
        }}>
        <Button
          variant="secondary"
          label="Cancel"
          fullWidth={false}
          skinny={false}
          sx={{ width: '50%' }}
          onClick={() => {
            onCancel?.();
          }}
        />
        <Button
          variant="primary"
          label="Save"
          fullWidth={false}
          skinny={false}
          sx={{ width: '50%' }}
          onClick={onSubmit}
        />
      </Box>
    </ModalContentContainer>
  );
};
